import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ChevronRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

export default function ContractValueDashboard({ cur = "EUR" }) {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(String(thisYear));
  const [data, setData] = useState(null);
  const [openClient, setOpenClient] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/reports/contract-value?year=${year}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [year]);

  if (data === null) return <div className="py-16 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  const t = data.totals;

  return (
    <div data-testid="contract-value-dashboard">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <p className="text-sm text-slate-500">Committed recurring income from valued recurring works — how it lands per client and per month.</p>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger data-testid="cv-year" className="w-[110px] h-9 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{[thisYear, thisYear + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[["Committed value", money(t.annual_value, cur), "accent-text"],
          ["Valued recurring works", t.works, "text-slate-800"],
          ["Visits in year", t.visits, "text-slate-800"],
          ["Unvalued works", t.unvalued_works, t.unvalued_works ? "text-amber-600" : "text-slate-800"]].map(([l, v, cls]) => (
          <div key={l} className="bg-white border border-slate-200 rounded-md p-4">
            <div className="text-xs text-slate-500">{l}</div>
            <div className={`text-2xl font-head font-bold mt-1 ${cls}`} data-testid={`cv-kpi-${l.toLowerCase().replace(/ /g, "-")}`}>{v}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <h3 className="font-head font-semibold text-sm mb-3">Committed revenue by month · {data.year}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.months}>
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={52} tickFormatter={(v) => money(v, cur).replace(/\.00$/, "")} />
              <Tooltip formatter={(v) => money(v, cur)} />
              <Bar dataKey="value" name="Committed" fill="var(--client-accent)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <h3 className="font-head font-semibold text-sm px-4 pt-4 pb-2">Per client · {data.year}</h3>
          <div className="max-h-[280px] overflow-y-auto scroll-thin">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0"><tr>{["Client", "Works", "Visits", "Annual value", ""].map((h) => <th key={h} className="text-left font-medium text-slate-500 px-3 py-2 text-xs">{h}</th>)}</tr></thead>
              <tbody>
                {data.clients.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-400">No valued recurring works for {data.year}.</td></tr>}
                {data.clients.map((c) => (
                  <>
                    <tr key={c.customer_name} data-testid={`cv-client-${c.customer_name}`} className="border-t border-slate-100 cursor-pointer hover:bg-slate-50" onClick={() => setOpenClient(openClient === c.customer_name ? null : c.customer_name)}>
                      <td className="px-3 py-2 font-medium text-slate-800"><ChevronRight className={`h-3.5 w-3.5 inline mr-1 text-slate-400 transition-transform ${openClient === c.customer_name ? "rotate-90" : ""}`} />{c.customer_name}</td>
                      <td className="px-3 py-2 text-slate-600">{c.works}</td>
                      <td className="px-3 py-2 text-slate-600">{c.visits_year}</td>
                      <td className="px-3 py-2 font-semibold text-slate-800">{money(c.annual_value, cur)}</td>
                      <td></td>
                    </tr>
                    {openClient === c.customer_name && c.items.map((it) => (
                      <tr key={it.ref} className="border-t border-slate-50 bg-slate-50/60 text-xs">
                        <td className="pl-9 pr-3 py-1.5 text-slate-600">{it.ref} · {it.title}</td>
                        <td className="px-3 py-1.5 text-slate-500">{it.frequency}</td>
                        <td className="px-3 py-1.5 text-slate-500">{it.visits_year} × {money(it.per_visit, cur)}</td>
                        <td className="px-3 py-1.5 text-slate-600">{money(it.annual_value, cur)}</td>
                        <td></td>
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
              {data.clients.length > 0 && (
                <tfoot><tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
                  <td className="px-3 py-2 text-slate-800">Grand total</td>
                  <td className="px-3 py-2">{t.works}</td>
                  <td className="px-3 py-2">{t.visits}</td>
                  <td className="px-3 py-2 accent-text" data-testid="cv-grand-total">{money(t.annual_value, cur)}</td>
                  <td></td>
                </tr></tfoot>
              )}
            </table>
          </div>
        </div>
      </div>

      {data.unvalued.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-md p-4 mt-4" data-testid="cv-unvalued">
          <h3 className="font-head font-semibold text-sm text-amber-700 mb-2">Unvalued recurring works ({data.unvalued.length})</h3>
          <p className="text-xs text-slate-500 mb-2">These have no contract value set — their income isn't counted above. Edit each work to add a value.</p>
          <div className="flex flex-wrap gap-2">
            {data.unvalued.map((u) => (
              <span key={u.ref} className="inline-flex items-center gap-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-full px-2.5 py-1">
                <span className="font-medium">{u.ref}</span>{u.title} · {u.customer_name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
